"use client";

import Lenis from "lenis";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cancelFrame, frame, useReducedMotion } from "framer-motion";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getNavbarOffset } from "@/lib/get-navbar-offset";

export interface ScrollToOptions {
  offset?: number;
  align?: "start" | "center";
}

interface ScrollContextValue {
  scrollY: number;
  progress: number;
  scrollToElement: (
    target: HTMLElement | string,
    options?: ScrollToOptions | number
  ) => void;
  scrollToTop: () => void;
}

interface ScrollLockContextValue {
  setScrollLocked: (locked: boolean) => void;
}

const ScrollContext = createContext<ScrollContextValue>({
  scrollY: 0,
  progress: 0,
  scrollToElement: () => {},
  scrollToTop: () => {},
});

const ScrollLockContext = createContext<ScrollLockContextValue>({
  setScrollLocked: () => {},
});

export function useScrollContext() {
  return useContext(ScrollContext);
}

export function useScrollLock() {
  return useContext(ScrollLockContext);
}

function resolveElement(target: HTMLElement | string) {
  return typeof target === "string"
    ? document.querySelector<HTMLElement>(target)
    : target;
}

function computeScrollTop(
  el: HTMLElement,
  { offset, align = "start" }: ScrollToOptions
) {
  const resolvedOffset = offset ?? -getNavbarOffset();
  if (align === "center") {
    return (
      el.getBoundingClientRect().top +
      window.scrollY -
      window.innerHeight / 2 +
      el.offsetHeight / 2
    );
  }

  return el.getBoundingClientRect().top + window.scrollY + resolvedOffset;
}

interface SmoothScrollProviderProps {
  children: ReactNode;
  /** When false, uses native scroll (lighter pages). Default true. */
  smoothScrollEnabled?: boolean;
}

export function SmoothScrollProvider({
  children,
  smoothScrollEnabled = true,
}: SmoothScrollProviderProps) {
  const reduced = useReducedMotion();
  const lenisRef = useRef<Lenis | null>(null);
  const pendingUpdateRef = useRef<number | null>(null);
  const [scrollState, setScrollState] = useState({ scrollY: 0, progress: 0 });
  const [scrollLocked, setScrollLocked] = useState(false);

  const scrollToTop = useCallback(() => {
    const lenis = lenisRef.current;
    if (lenis) {
      lenis.scrollTo(0);
      return;
    }
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  }, [reduced]);

  const scrollToElement = useCallback(
    (target: HTMLElement | string, options?: ScrollToOptions | number) => {
      const resolvedOptions: ScrollToOptions =
        typeof options === "number" ? { offset: options } : (options ?? {});

      const el = resolveElement(target);
      if (!el) return;

      const top = computeScrollTop(el, resolvedOptions);
      const lenis = lenisRef.current;

      if (lenis) {
        lenis.scrollTo(top);
        return;
      }

      window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
    },
    [reduced]
  );

  useEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;

    if (scrollLocked) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }, [scrollLocked]);

  useEffect(() => {
    const useNativeScroll = reduced || !smoothScrollEnabled;

    if (useNativeScroll) {
      const onScroll = () => {
        const scrollY = window.scrollY;
        const maxScroll =
          document.documentElement.scrollHeight - window.innerHeight;
        const progress = maxScroll > 0 ? scrollY / maxScroll : 0;
        setScrollState({ scrollY, progress });
      };

      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const lenis = new Lenis({
      duration: 1,
      smoothWheel: true,
      syncTouch: false,
      autoRaf: false,
    });

    lenisRef.current = lenis;
    document.documentElement.classList.add("lenis", "lenis-smooth");

    const scheduleUpdate = (scrollY: number, progress: number) => {
      if (pendingUpdateRef.current !== null) return;
      pendingUpdateRef.current = requestAnimationFrame(() => {
        pendingUpdateRef.current = null;
        setScrollState({ scrollY, progress });
      });
    };

    lenis.on("scroll", (instance) => {
      scheduleUpdate(instance.scroll, instance.progress);
      ScrollTrigger.update();
    });

    const onFrame = (data: { timestamp: number }) => {
      lenis.raf(data.timestamp);
    };
    frame.update(onFrame, true);

    const onAnchorClick = (e: MouseEvent) => {
      const anchor = (e.target as Element).closest<HTMLAnchorElement>(
        'a[href^="#"]'
      );
      if (!anchor) return;

      const hash = anchor.getAttribute("href");
      if (!hash || hash === "#") return;

      const target = document.querySelector<HTMLElement>(hash);
      if (!target) return;

      e.preventDefault();
      scrollToElement(target, { offset: -getNavbarOffset() });
    };

    document.addEventListener("click", onAnchorClick);

    return () => {
      document.removeEventListener("click", onAnchorClick);
      cancelFrame(onFrame);
      if (pendingUpdateRef.current !== null) {
        cancelAnimationFrame(pendingUpdateRef.current);
      }
      document.documentElement.classList.remove("lenis", "lenis-smooth");
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reduced, smoothScrollEnabled, scrollToElement]);

  return (
    <ScrollLockContext.Provider value={{ setScrollLocked }}>
      <ScrollContext.Provider
        value={{
          scrollY: scrollState.scrollY,
          progress: scrollState.progress,
          scrollToElement,
          scrollToTop,
        }}
      >
        {children}
      </ScrollContext.Provider>
    </ScrollLockContext.Provider>
  );
}
